// Las decisiones de una escena (T-001-05).
//
// La pregunta que encabeza una decisión NO se escribe aquí: es la línea que
// el guion pone justo antes (por ejemplo `s03_n002`, «¿Cómo quieres
// saludarme?»), que se mantiene en pantalla mientras el niño elige. Antes
// este componente mostraba un rótulo propio y el niño dejaba de ver la
// pregunta del contenido justo en el momento de decidir: eso es contenido
// infantil escrito en código, y lo bloqueó la revisión de content-guardian
// (AGENTS.md; Constitución IV).
//
// Cada opción muestra un asset de imagen encima del texto. El texto DOM sigue
// siendo el nombre accesible y principal de la decisión.
//
// Nunca solo icono y nunca solo color: la forma del icono, el texto y el orden
// son tres señales distintas (Constitución VII). El icono va `aria-hidden`
// porque el texto de la tarjeta ya es su nombre accesible; repetirlo obligaría
// a escucharlo dos veces. El disco de color detrás del icono es decoración.
//
// No hay opción correcta ni incorrecta: aquí no existe estado de acierto, ni
// de error, ni marca de intento (Constitución V, AC-2 y AC-4). Elegir otra
// vez es simplemente volver a pulsar.

import type { ChoiceView } from "../../engine";
import { obtenerArteDeTarjeta, UI_CARD_ASSETS } from "../../shared/assets";
import { IlustracionDeTarjeta } from "../ui/IlustracionDeTarjeta";
import { IdentidadDeHablante } from "./TurnoDeDialogo";
import { ladoParaVozUnica } from "./lado-dialogo";

interface DecisionesProps {
  readonly vista: ChoiceView;
  readonly alElegir: (optionId: string) => void;
}

const TONOS_DE_TARJETA = ["sol", "coral", "cielo", "menta", "lila"] as const;
export function Decisiones({ vista, alElegir }: DecisionesProps) {
  // La pregunta la trae la vista desde el motor: es la última línea que se
  // mostró en esta escena, no un rótulo de la interfaz.
  const pregunta = vista.precedingLine;
  const idPregunta = `decision-${vista.node.id}-pregunta`;
  const idNota = "decision-" + vista.node.id + "-nota";
  const claseDecisiones = [
    "decisiones",
    "decisiones--visual",
    vista.options.length > 3 ? "decisiones--muchas-opciones" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    // `role="group"` y no el `region` implícito de `<section>` con nombre:
    // cada decisión es un grupo de controles relacionados, no una zona de la
    // página. Llenar la lista de puntos de referencia con una región por
    // decisión estorbaría a quien navega por landmarks.
    //
    // El grupo se nombra con la línea del guion cuando existe. Si no existe
    // (se entró directo a la decisión con el selector de escena de
    // desarrollo), queda sin nombre: inventarle uno sería volver a poner
    // texto para un niño en el código.
    <section
      className={claseDecisiones}
      role="group"
      {...(pregunta ? { "aria-labelledby": idPregunta } : {})}
      {...(pregunta ? { "aria-describedby": idNota } : {})}
    >
      {pregunta ? (
        <div className="decisiones__pregunta" data-lado={ladoParaVozUnica(pregunta.speaker)}>
          <div className="dialogo__hablante decisiones__hablante">
            <IdentidadDeHablante speaker={pregunta.speaker} nombre={pregunta.speakerName} />
          </div>
          {/* La pregunta del guion es además el encabezado de la pantalla.
              Al quitar el rótulo inventado, la decisión se quedó sin ningún
              encabezado y quien navega con la tecla H perdía el tramo central
              del episodio (revisión de a11y-perf-reviewer). Se marca como
              `h2` el texto que ya estaba, sin escribir texto nuevo. */}
          <h2 className="dialogo__texto decisiones__titulo" id={idPregunta}>
            {pregunta.text}
          </h2>
        </div>
      ) : null}

      <ul className="decisiones__lista">
        {vista.options.map(({ option, text }, indice) => {
          const ilustracion = obtenerArteDeTarjeta(UI_CARD_ASSETS.icons, option.icon);
          const tono = TONOS_DE_TARJETA[indice % TONOS_DE_TARJETA.length] ?? "sol";
          return (
            <li key={option.id}>
              <button
                type="button"
                className="objetivo-tactil boton boton--opcion"
                data-tono={tono}
                data-principal={indice === 0 ? "true" : undefined}
                onClick={() => alElegir(option.id)}
              >
                {ilustracion ? <IlustracionDeTarjeta src={ilustracion} /> : null}
                <span>{text}</span>
              </button>
            </li>
          );
        })}
      </ul>
      <p className="decisiones__nota" id={idNota}>
        Puedes elegir lo que te resulte cómodo.
      </p>
    </section>
  );
}
